import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper, List, ListItem, ListItemText,
  ListItemIcon, IconButton, Tooltip, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Alert, Collapse, TextField, Menu, MenuItem, Divider,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Warning as WarningIcon,
  CloudDownload as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  FolderOpen as FolderOpenIcon,
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  UploadFile as UploadFileIcon,
  InsertDriveFile as FileIcon,
  FolderZip as ZipIcon,
  CreateNewFolder as CreateNewFolderIcon,
  MoreVert as MoreVertIcon,
  DriveFileRenameOutline as RenameIcon,
  DeleteOutline as DeleteIcon,
  DragIndicator as DragIcon,
  Delete,
  Description as GoogleDocIcon,
  AutoAwesome as AutoAwesomeIcon,
  CreateNewFolder as AddSectionIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { formatDate } from '../../constants/DateUtils';
import { formatFileSize, saveDocumentMetadata } from '../../constants/CommonFunctions';
import { useTranslations } from '../../constants/translations/CaseTranslation';
import DocumentUploadModal from '../../components/Modal/DocumentUploadModal';
import ZipUploadModal from '../../components/Modal/ZipUploadModal';
import DocumentMetadataModal from '../../components/Modal/DocumentMetadataModal';
import { useFolders, buildTree } from '../../hooks/useFolders';
import { analyzeDocument } from '../../hooks/useDocumentAnalysis';

// ─── Utilidades ─────────────────────────────────────────────────────────────

const DND = { DOC: 'document', FOLDER: 'folder' };
const SPACE_ROOT_PREFIX = 'space-root:';
const spaceRootId = (spaceId) => `${SPACE_ROOT_PREFIX}${spaceId}`;

function isWordDoc(doc) {
  const ext = doc.extension?.toLowerCase() || '';
  const mime = doc.mime_type?.toLowerCase() || '';
  return ['doc','docx'].includes(ext) || ['word','msword','wordprocessingml'].some(m => mime.includes(m));
}
function isGoogleDoc(doc) { return doc.mime_type?.toLowerCase().includes('google-apps'); }
function getDriveUrl(doc, edit = false) {
  if (!doc.google_drive_id && !doc.drive_url) return null;
  if (edit || isWordDoc(doc)) return `https://docs.google.com/document/d/${doc.google_drive_id}/edit`;
  if (isGoogleDoc(doc)) return doc.drive_url?.replace('/view','/edit') || `https://docs.google.com/document/d/${doc.google_drive_id}/edit`;
  return doc.drive_url || `https://drive.google.com/file/d/${doc.google_drive_id}/view`;
}
function countDocs(node) {
  return node.documents.length + node.children.reduce((s,c) => s + countDocs(c), 0);
}

function dropPosition(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  return e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
}

function FolderNameDialog({ open, onClose, onConfirm, title, initialValue = '', officeColor, label = 'Nombre' }) {
  const [name, setName] = useState('');
  const [err,  setErr]  = useState('');

  const reset = () => { setName(initialValue); setErr(''); };

  const confirm = async () => {
    if (!name.trim())         { setErr('El nombre no puede estar vacío'); return; }
    if (name.trim().length > 128) { setErr('Máximo 128 caracteres'); return; }
    try { await onConfirm(name.trim()); onClose(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      TransitionProps={{ onEnter: reset }}>
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <TextField autoFocus fullWidth size="small" label={label}
          value={name} onChange={e => { setName(e.target.value); setErr(''); }}
          error={!!err} helperText={err}
          onKeyDown={e => e.key === 'Enter' && confirm()}
          sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: officeColor } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">Cancelar</Button>
        <Button onClick={confirm} variant="contained" size="small"
          sx={{ backgroundColor: officeColor, '&:hover': { backgroundColor: officeColor, opacity: 0.9 } }}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── CreateGoogleDocDialog ───────────────────────────────────────────────────

function CreateGoogleDocDialog({ open, onClose, onConfirm, officeColor, flatFolders, spaces, creating }) {
  const [name,     setName]     = useState('');
  const [folderId, setFolderId] = useState('');
  const [err,      setErr]      = useState('');

  const spaceNameById = useMemo(() => {
    const map = {};
    for (const s of spaces || []) map[s.id] = s.name;
    return map;
  }, [spaces]);

  const reset = () => { setName(''); setFolderId(''); setErr(''); };

  const confirm = async () => {
    if (!name.trim()) { setErr('El nombre no puede estar vacío'); return; }
    if (name.trim().length > 200) { setErr('Máximo 200 caracteres'); return; }
    try {
      await onConfirm(name.trim(), folderId || null);
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <Dialog open={open} onClose={!creating ? onClose : undefined} maxWidth="xs" fullWidth
      TransitionProps={{ onEnter: reset }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <GoogleDocIcon sx={{ color: '#4285f4' }} />
        Nuevo Google Doc
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Se creará un documento en blanco en Google Drive, vinculado a este expediente. Podrás editarlo directamente desde Drive.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Nombre del documento"
          value={name}
          onChange={e => { setName(e.target.value); setErr(''); }}
          error={!!err}
          helperText={err}
          disabled={creating}
          onKeyDown={e => e.key === 'Enter' && confirm()}
          sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: officeColor } }}
        />
        {flatFolders?.length > 0 && (
          <FormControl fullWidth size="small">
            <InputLabel>Carpeta (opcional)</InputLabel>
            <Select
              value={folderId}
              label="Carpeta (opcional)"
              onChange={e => setFolderId(e.target.value)}
              disabled={creating}
            >
              <MenuItem value=""><em>Raíz del expediente</em></MenuItem>
              {flatFolders.map(f => (
                <MenuItem key={f.id} value={f.id}>
                  {'  '.repeat(f.depth || 0)}{f.depth > 0 ? '└ ' : ''}{f.name}
                  {spaceNameById[f.space_id] ? ` (${spaceNameById[f.space_id]})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {creating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4285f4' }}>
            <CircularProgress size={16} sx={{ color: '#4285f4' }} />
            <Typography variant="body2">Creando documento en Google Drive...</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small" disabled={creating}>
          Cancelar
        </Button>
        <Button
          onClick={confirm}
          variant="contained"
          size="small"
          disabled={creating || !name.trim()}
          startIcon={creating ? <CircularProgress size={14} /> : <GoogleDocIcon />}
          sx={{ backgroundColor: '#4285f4', '&:hover': { backgroundColor: '#3367d6' } }}
        >
          {creating ? 'Creando...' : 'Crear documento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── DocumentRow ─────────────────────────────────────────────────────────────

function DocumentRow({ doc, officeColor, onAction, downloading, caseRole, isReadOnly, caseData, t, isDragOver, analyzing }) {
  const inDrive = !!(doc.drive_url || doc.google_drive_id);
  const isPdf = doc.mime_type === 'application/pdf' || doc.extension?.toLowerCase() === 'pdf';

  return (
    <ListItem
      draggable={!isReadOnly && caseRole !== 'client'}
      data-dtype={DND.DOC}
      data-did={doc.id}
      sx={{
        pl: 1, pr: 1, py: 0.4, borderRadius: 1, mb: 0.2,
        cursor: isReadOnly ? 'default' : 'grab',
        border: isDragOver ? `2px dashed ${officeColor}` : '2px solid transparent',
        backgroundColor: isDragOver ? `${officeColor}10` : 'transparent',
        transition: 'all 0.12s',
        '&:hover': { backgroundColor: '#f7f8fa' },
      }}
      secondaryAction={
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          {isPdf && inDrive && (
            <Tooltip title="Analizar con IA">
              <IconButton
                size="small"
                onClick={() => onAction('analyze', doc)}
                disabled={analyzing}
                sx={{ color: '#6c72ff' }}
              >
                {analyzing ? <CircularProgress size={14} /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
          {inDrive && (
            <Tooltip title="Abrir en Drive">
              <IconButton size="small" onClick={() => window.open(getDriveUrl(doc), '_blank', 'noopener')} sx={{ color: '#4285f4' }}>
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {inDrive && (isWordDoc(doc) || isGoogleDoc(doc)) && (
            <Tooltip title="Editar en Google Docs">
              <IconButton size="small" onClick={() => window.open(getDriveUrl(doc,true), '_blank', 'noopener')} sx={{ color: '#4285f4' }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Descargar">
            <IconButton size="small" onClick={() => onAction('download', doc)} disabled={downloading[doc.id]} sx={{ color: officeColor }}>
              {downloading[doc.id] ? <CircularProgress size={14}/> : <DownloadIcon sx={{ fontSize: 16 }}/>}
            </IconButton>
          </Tooltip>
          {caseRole !== 'client' && !isReadOnly && (
            doc.is_archived
              ? <Tooltip title="Desarchivar"><IconButton size="small" onClick={() => onAction('unarchive',doc)} sx={{ color:'#4caf50' }}><UnarchiveIcon sx={{ fontSize:16 }}/></IconButton></Tooltip>
              : <Tooltip title="Archivar"><IconButton size="small" onClick={() => onAction('archive',doc)} sx={{ color:'#ff9800' }}><ArchiveIcon sx={{ fontSize:16 }}/></IconButton></Tooltip>
          )}
          {caseRole !== 'client' && !isReadOnly && (
            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={() => onAction('delete', doc)}
                sx={{ color: '#f44336' }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }
    >
      {!isReadOnly && caseRole !== 'client' && (
        <Box sx={{ color:'#d0d5dd', mr: 0.5, display:'flex', alignItems:'center' }}>
          <DragIcon sx={{ fontSize:15 }}/>
        </Box>
      )}
      <ListItemIcon sx={{ minWidth: 24 }}>
        <FileIcon sx={{ fontSize: 16, color: inDrive ? '#4285f4' : '#90a4ae' }}/>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display:'flex', alignItems:'center', gap: 0.6, flexWrap:'wrap', pr: 13 }}>
            <Typography variant="body2" sx={{ fontSize:'0.81rem', fontWeight: 500 }}>
              {doc.name || doc.filename}
            </Typography>
            {doc.extension && <Chip label={doc.extension.toUpperCase()} size="small" sx={{ height:14, fontSize:'0.58rem', backgroundColor:'#f0f0f0' }}/>}
            {inDrive       && <Chip label="Drive" size="small" sx={{ height:14, fontSize:'0.58rem', backgroundColor:'#e8f0fe', color:'#4285f4' }}/>}
            {doc.is_archived && <Chip label="Archivado" size="small" sx={{ height:14, fontSize:'0.58rem', backgroundColor:'#fff3e0', color:'#ff9800' }}/>}
          </Box>
        }
        secondary={
          <Box sx={{ display:'flex', gap:1.5, mt: 0.1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize:'0.68rem' }}>
              {formatDate(doc.created_at, caseData?.language)}
            </Typography>
            {doc.size_bytes && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize:'0.68rem' }}>
                {formatFileSize(parseInt(doc.size_bytes))}
              </Typography>
            )}
          </Box>
        }
        secondaryTypographyProps={{ component: 'div' }}
      />
    </ListItem>
  );
}

// ─── FolderNode ──────────────────────────────────────────────────────────────

function FolderNode({ node, depth, officeColor, onDocAction, onFolderAction, downloading,
  caseRole, isReadOnly, caseData, t, dragState, onDragStart, onDragEnter, onDragLeave, onDrop, analyzingId }) {

  const [open, setOpen]           = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const isRoot      = depth === 0;
  const total       = countDocs(node);
  const children    = [...node.children].sort((a,b) => a.name.localeCompare(b.name));

  const isOver = dragState.overId === node.id;
  const isDraggingThis = dragState.type === DND.FOLDER && dragState.id === node.id;

  const dropZoneStyle = {
    border: isOver ? `2px dashed ${officeColor}` : '2px solid transparent',
    backgroundColor: isOver ? `${officeColor}0e` : 'transparent',
    transition: 'all 0.12s',
    borderRadius: 1,
  };

  const folderFallbackDropProps = (!isRoot && !isReadOnly && caseRole !== 'client')
    ? {
        onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); onDragEnter(node.id); },
        onDrop: (e) => { e.preventDefault(); e.stopPropagation(); onDrop(node.id); },
      }
    : {};

  return (
    <Box sx={{ opacity: isDraggingThis ? 0.35 : 1, transition: 'opacity 0.12s' }} {...folderFallbackDropProps}>
      {/* ── Cabecera de carpeta (oculta para raíz) ── */}
      {!isRoot && (
        <Box
          draggable={!isReadOnly && caseRole !== 'client'}
          onDragStart={(e) => { e.stopPropagation(); onDragStart(e, node.id, DND.FOLDER); }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); onDragEnter(node.id); }}
          onDragOver={(e)  => { e.preventDefault(); e.stopPropagation(); onDragEnter(node.id); }}
          onDragLeave={(e) => { e.stopPropagation(); onDragLeave(node.id); }}
          onDrop={(e)      => { e.preventDefault(); e.stopPropagation(); onDrop(node.id); }}
          sx={{
            ...dropZoneStyle,
            display:'flex', alignItems:'center', gap: 0.75,
            py: 0.85, px: 0.75,
            cursor: isReadOnly ? 'default' : 'grab',
            '&:hover': { backgroundColor: isOver ? `${officeColor}0e` : `${officeColor}07`,
              '& .fa-btn': { opacity: 1 } },
            '& .fa-btn': { opacity: 0, transition: 'opacity 0.12s' },
          }}
        >
          {!isReadOnly && caseRole !== 'client' && (
            <Box sx={{ color:'#d0d5dd', lineHeight:1, flexShrink: 0 }}>
              <DragIcon sx={{ fontSize:14 }}/>
            </Box>
          )}
          <IconButton size="small" sx={{ p: 0.15, color: officeColor, flexShrink: 0 }}
            onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
            {open ? <ExpandMoreIcon sx={{ fontSize:18 }}/> : <ChevronRightIcon sx={{ fontSize:18 }}/>}
          </IconButton>
          {open
            ? <FolderOpenIcon sx={{ color: officeColor, fontSize:24, flexShrink:0 }}/>
            : <FolderIcon     sx={{ color: officeColor, fontSize:24, flexShrink:0 }}/>
          }
          <Typography variant="body1" fontWeight={700} sx={{ flex:1, fontSize:'0.95rem', cursor:'pointer' }}
            onClick={() => setOpen(o => !o)}>
            {node.name}
          </Typography>
          {total > 0 && (
            <Chip label={total} size="small"
              sx={{ height:20, fontSize:'0.68rem', backgroundColor:`${officeColor}18`, color: officeColor, fontWeight:'bold', mr:0.25 }}/>
          )}
          {!isReadOnly && caseRole !== 'client' && (
            <Box className="fa-btn">
              <IconButton size="small" sx={{ p:0.2, color:'#9e9e9e' }}
                onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
                <MoreVertIcon sx={{ fontSize:15 }}/>
              </IconButton>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}
                PaperProps={{ sx: { boxShadow:'0 4px 20px rgba(0,0,0,0.1)', borderRadius:2, minWidth:170 } }}>
                <MenuItem dense onClick={() => { setMenuAnchor(null); onFolderAction('sub', node); }}>
                  <CreateNewFolderIcon sx={{ fontSize:16, mr:1.5, color: officeColor }}/>
                  <Typography variant="body2">Nueva subcarpeta</Typography>
                </MenuItem>
                <MenuItem dense onClick={() => { setMenuAnchor(null); onFolderAction('rename', node); }}>
                  <RenameIcon sx={{ fontSize:16, mr:1.5, color:'#607d8b' }}/>
                  <Typography variant="body2">Renombrar</Typography>
                </MenuItem>
                <Divider/>
                <MenuItem dense onClick={() => { setMenuAnchor(null); onFolderAction('delete', node); }}
                  sx={{ color:'#f44336' }}>
                  <DeleteIcon sx={{ fontSize:16, mr:1.5 }}/>
                  <Typography variant="body2">Eliminar carpeta</Typography>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      )}

      {/* ── Zona de drop para raíz ── */}
      {isRoot && dragState.id && (
        <Box
          onDragEnter={e => { e.preventDefault(); onDragEnter(node.id); }}
          onDragOver={e  => { e.preventDefault(); onDragEnter(node.id); }}
          onDragLeave={() => onDragLeave(node.id)}
          onDrop={e => { e.preventDefault(); onDrop(node.id); }}
          sx={{
            ...dropZoneStyle,
            border: isOver ? `2px dashed ${officeColor}` : '2px solid transparent',
            backgroundColor: isOver ? `${officeColor}0e` : 'transparent',
            py: isOver ? 1 : 0.25,
            mb: 0.5, textAlign:'center'
          }}
        >
          {isOver && (
            <Typography variant="caption" sx={{ color: officeColor, fontWeight:600 }}>
              Mover a raíz de "{node.spaceName}"
            </Typography>
          )}
        </Box>
      )}

      <Collapse in={isRoot || open}>
        <Box sx={{ pl: isRoot ? 0 : 2 }}>
          {/* Sub-carpetas */}
          {children.map(child => (
            <FolderNode key={child.id} node={child} depth={depth + 1}
              officeColor={officeColor} onDocAction={onDocAction} onFolderAction={onFolderAction}
              downloading={downloading} caseRole={caseRole} isReadOnly={isReadOnly}
              caseData={caseData} t={t} dragState={dragState}
              onDragStart={onDragStart} onDragEnter={onDragEnter}
              onDragLeave={onDragLeave} onDrop={onDrop}
              analyzingId={analyzingId}
            />
          ))}

          {/* Documentos */}
          {node.documents.length > 0 && (
            <List dense disablePadding>
              {node.documents.map(doc => {
                const isOverThis = dragState.overId === doc.id;
                const isDocDrag = dragState.type === DND.DOC;
                const isDraggingThisDoc = isDocDrag && dragState.id === doc.id;
                return (
                <Box key={doc.id}
                  onDragStart={e => onDragStart(e, doc.id, DND.DOC)}
                  onDragEnter={e => { e.preventDefault(); e.stopPropagation(); onDragEnter(doc.id, isDocDrag ? dropPosition(e) : null); }}
                  onDragOver={e  => { e.preventDefault(); e.stopPropagation(); onDragEnter(doc.id, isDocDrag ? dropPosition(e) : null); }}
                  onDragLeave={e => { e.stopPropagation(); onDragLeave(doc.id); }}
                  onDrop={e => {
                    e.preventDefault(); e.stopPropagation();
                    if (dragState.type === DND.FOLDER) onDrop(node.id);
                    else onDrop(`doc:${doc.id}:${dropPosition(e)}`);
                  }}
                  sx={{
                    opacity: isDraggingThisDoc ? 0.35 : 1,
                    transition: 'opacity 0.12s',
                    ...(isOverThis && isDocDrag ? {
                      borderTop: dragState.overPosition === 'before' ? `2px solid ${officeColor}` : '2px solid transparent',
                      borderBottom: dragState.overPosition === 'after' ? `2px solid ${officeColor}` : '2px solid transparent',
                    } : { borderTop: '2px solid transparent', borderBottom: '2px solid transparent' }),
                  }}
                >
                  <DocumentRow doc={doc} officeColor={officeColor} onAction={onDocAction}
                    downloading={downloading} caseRole={caseRole} isReadOnly={isReadOnly}
                    caseData={caseData} t={t}
                    isDragOver={isOverThis && !isDocDrag}
                    analyzing={analyzingId === doc.id}
                  />
                </Box>
              );})}
            </List>
          )}

          {/* Carpeta vacía — drop zone */}
          {!isRoot && node.documents.length === 0 && children.length === 0 && (
            <Box
              onDragEnter={e => { e.preventDefault(); onDragEnter(node.id); }}
              onDragOver={e  => { e.preventDefault(); onDragEnter(node.id); }}
              onDragLeave={() => onDragLeave(node.id)}
              onDrop={e => { e.preventDefault(); e.stopPropagation(); onDrop(node.id); }}
              sx={{ ...dropZoneStyle, py: 1, px: 2, border: isOver ? `2px dashed ${officeColor}` : '2px dashed #e0e0e0' }}
            >
              <Typography variant="caption" color={isOver ? officeColor : 'text.disabled'} sx={{ fontStyle:'italic' }}>
                {isOver ? '📁 Soltar aquí' : 'Carpeta vacía — arrastra archivos aquí'}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── CaseDocumentSection ─────────────────────────────────────────────────────

export default function CaseDocumentSection({
  documents: allDocuments, onDocumentsChange,
  caseData, office, caseRole, currentUser, onUploadDocument, readOnly = false
}) {
  const t           = useTranslations(caseData?.language);
  const officeColor = office?.color || '#1976d2';

  // UI state
  const [openUpload,    setOpenUpload]    = useState(false);
  const [openZip,       setOpenZip]       = useState(false);
  const [showArchived,  setShowArchived]  = useState(false);
  const [downloading,   setDownloading]   = useState({});
  const [storageType,   setStorageType]   = useState('gdrive');

  const [openGoogleDocDlg, setOpenGoogleDocDlg] = useState(false);
  const [creatingGoogleDoc, setCreatingGoogleDoc] = useState(false);
  const [googleDocError, setGoogleDocError] = useState('');

  // Formulario opcional de metadata (post-subida), para el copiloto IA
  const [metadataDoc, setMetadataDoc] = useState(null); // { id, name }
  const [savingMetadata, setSavingMetadata] = useState(false);
  const metadataResolveRef = useRef(null);

  const promptForMetadata = (doc) =>
    new Promise((resolve) => {
      metadataResolveRef.current = resolve;
      setMetadataDoc(doc);
    });

  const resolveMetadataPrompt = () => {
    setMetadataDoc(null);
    const resolve = metadataResolveRef.current;
    metadataResolveRef.current = null;
    resolve?.();
  };

  const handleMetadataSkip = () => resolveMetadataPrompt();

  const handleMetadataSave = async (form) => {
    setSavingMetadata(true);
    try {
      await saveDocumentMetadata(metadataDoc?.id, caseData.id, form);
    } catch (err) {
      console.error('Error al guardar metadata del documento:', err);
    } finally {
      setSavingMetadata(false);
      resolveMetadataPrompt();
    }
  };

  // Folder dialogs
  const [createDlg, setCreateDlg] = useState({ open:false, parentId:null, title:'', spaceId:null });
  const [uploadSpaceId, setUploadSpaceId] = useState(null);
  const [renameDlg, setRenameDlg] = useState({ open:false, folder:null });
  const [deleteDlg, setDeleteDlg] = useState({ open:false, folder:null, deleting:false });
  const [deleteDocDlg, setDeleteDocDlg] = useState({ open: false, doc: null, deleting: false });

  // Diálogos de sección (árbol de documentos)
  const [createSpaceDlg, setCreateSpaceDlg] = useState(false);
  const [renameSpaceDlg, setRenameSpaceDlg] = useState({ open:false, space:null });
  const [deleteSpaceDlg, setDeleteSpaceDlg] = useState({ open:false, space:null, deleting:false });

  // Archive dialog
  const [archiveDlg, setArchiveDlg] = useState({ open:false, doc:null, archiving:false });

  // Drag state
  const [drag, setDrag] = useState({ id:null, type:null, overId:null, overPosition:null });

  const [analyzingId, setAnalyzingId] = useState(null);
  const [analysisDlg, setAnalysisDlg] = useState({ open: false, doc: null, text: '', loading: false, error: '' });

  const { folders, folderMap, flatFolders, spaces, loadFolders,
    createSpace, renameSpace, deleteSpace,
    createFolder, renameFolder, deleteFolder, moveFolder, moveDocument, reorderDocuments
  } = useFolders(caseData?.id);

  // Load storage type
  useEffect(() => {
    if (!office?.organization_id) return;
    supabase.from('organization').select('storage_type')
      .eq('id', office.organization_id).single()
      .then(({ data }) => setStorageType(data?.storage_type || 'gdrive'))
      .catch(() => setStorageType('gdrive'));
  }, [office?.organization_id]);

  const isReadOnly = () => readOnly || caseData?.state === 'completed' || !caseData?.is_visible;

  const caseDocs   = useMemo(() => allDocuments.filter(d => !d.task_id), [allDocuments]);
  const activeDocs = useMemo(() => caseDocs.filter(d => !d.is_archived), [caseDocs]);
  const archDocs   = useMemo(() => caseDocs.filter(d => d.is_archived),  [caseDocs]);
  const display    = showArchived ? archDocs : activeDocs;

  const defaultSpaceId = spaces[0]?.id ?? null;

  const spaceSections = useMemo(() => {
    return spaces.map(space => {
      const spFolders = folders.filter(f => (f.space_id || defaultSpaceId) === space.id);
      const spDisplay = display.filter(d => (d.space_id || defaultSpaceId) === space.id);
      const { rootNodes, rootDocuments } = buildTree(spFolders, spDisplay);
      const rootId = spaceRootId(space.id);
      const vRoot = {
        id: rootId, name: '', parent_id: null, space_id: space.id, spaceName: space.name,
        children: rootNodes, documents: rootDocuments,
      };
      const isEmpty = spDisplay.length === 0 && spFolders.length === 0;
      const spAllDocs = caseDocs.filter(d => (d.space_id || defaultSpaceId) === space.id);
      const isTrulyEmpty = spAllDocs.length === 0 && spFolders.length === 0;
      return { space, rootId, folders: spFolders, display: spDisplay, vRoot, isEmpty, isTrulyEmpty };
    });
  }, [spaces, folders, display, caseDocs, defaultSpaceId]);

  const resolveDropTarget = useCallback((targetId) => {
    if (typeof targetId === 'string' && targetId.startsWith(SPACE_ROOT_PREFIX)) {
      return { folderId: null, spaceId: targetId.slice(SPACE_ROOT_PREFIX.length) };
    }
    const f = folderMap[targetId];
    return { folderId: targetId, spaceId: f?.space_id || defaultSpaceId };
  }, [folderMap, defaultSpaceId]);

  // ── Drag handlers ──────────────────────────────────────────────────────────

  const onDragStart = useCallback((e, id, type) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ id, type }));
    requestAnimationFrame(() => {
      setDrag(prev => ({ ...prev, id, type }));
    });
  }, []);

  const onDragEnter = useCallback((overId, position = null) => {
    setDrag(prev => (prev.overId === overId && prev.overPosition === position)
      ? prev : { ...prev, overId, overPosition: position });
  }, []);

  const onDragLeave = useCallback((leftId) => {
    setTimeout(() => {
      setDrag(prev => (prev.overId === leftId ? { ...prev, overId: null, overPosition: null } : prev));
    }, 30);
  }, []);

  const onDragEnd = useCallback(() => {
    setDrag({ id:null, type:null, overId:null, overPosition:null });
  }, []);

  const onDrop = useCallback(async (targetId) => {
    const { id, type } = drag;
    setDrag({ id:null, type:null, overId:null, overPosition:null });
    if (!id || !type) return;

    if (typeof targetId === 'string' && targetId.startsWith('doc:')) {
      if (type !== DND.DOC) return; // las carpetas no se reordenan así
      const [, targetDocId, position] = targetId.split(':');
      if (targetDocId === id) return;
      const targetDoc = display.find(d => d.id === targetDocId);
      const draggedDoc = display.find(d => d.id === id);
      if (!targetDoc || !draggedDoc) return;

      const groupFolderId = targetDoc.folder_id ?? null;
      const groupSpaceId = targetDoc.space_id || defaultSpaceId;

      const siblings = display
        .filter(d => d.id !== id && (d.folder_id ?? null) === groupFolderId && (d.space_id || defaultSpaceId) === groupSpaceId)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

      const targetIndex = siblings.findIndex(d => d.id === targetDocId);
      const insertAt = position === 'after' ? targetIndex + 1 : targetIndex;
      const orderedIds = siblings.map(s => s.id);
      orderedIds.splice(insertAt, 0, id);

      try {
        const crossing = (draggedDoc.folder_id ?? null) !== groupFolderId || (draggedDoc.space_id || defaultSpaceId) !== groupSpaceId;
        if (crossing) await moveDocument(id, groupFolderId, groupSpaceId);
        await reorderDocuments(orderedIds);
        if (onDocumentsChange) await onDocumentsChange();
      } catch (err) { alert('Error al reordenar: ' + err.message); }
      return;
    }

    const { folderId: normTarget, spaceId: targetSpaceId } = resolveDropTarget(targetId);

    if (type === DND.FOLDER) {
      const f = folderMap[id];
      if (!f) return;
      if (normTarget === id) return;
      const spaceChanges = (f.space_id || defaultSpaceId) !== targetSpaceId;
      if (f.parent_id === normTarget && !spaceChanges) return;
      try {
        await moveFolder(id, normTarget, folderMap, spaceChanges ? targetSpaceId : undefined);
        if (spaceChanges && onDocumentsChange) await onDocumentsChange();
      }
      catch (err) { alert(err.message); }
    } else {
      const doc = display.find(d => d.id === id);
      if (!doc) return;
      if (doc.folder_id === normTarget && (doc.space_id || defaultSpaceId) === targetSpaceId) return;
      try {
        await moveDocument(id, normTarget, targetSpaceId);
        if (onDocumentsChange) await onDocumentsChange();
      } catch (err) { alert('Error al mover: ' + err.message); }
    }
  }, [drag, folderMap, display, moveFolder, moveDocument, reorderDocuments, onDocumentsChange, resolveDropTarget, defaultSpaceId]);

  // ── Folder actions ─────────────────────────────────────────────────────────

  const onFolderAction = useCallback((action, node) => {
    if (action === 'sub')    setCreateDlg({ open:true, parentId:node.id, title:`Nueva carpeta en "${node.name}"`, spaceId: node.space_id || defaultSpaceId });
    if (action === 'rename') setRenameDlg({ open:true, folder: node });
    if (action === 'delete') setDeleteDlg({ open:true, folder: node, deleting:false });
  }, [defaultSpaceId]);

  const doCreate  = async (name) => createFolder(name, createDlg.parentId, currentUser?.id, createDlg.spaceId);
  const doRename  = async (name) => renameFolder(renameDlg.folder?.id, name);
  const doDelete  = async () => {
    setDeleteDlg(p => ({ ...p, deleting:true }));
    try {
      await deleteFolder(deleteDlg.folder?.id);
      if (onDocumentsChange) await onDocumentsChange();
      setDeleteDlg({ open:false, folder:null, deleting:false });
    } catch (e) {
      alert(e.message);
      setDeleteDlg(p => ({ ...p, deleting:false }));
    }
  };
  const doDeleteDocument = async () => {
    setDeleteDocDlg(p => ({ ...p, deleting: true }));

    try {
      const doc = deleteDocDlg.doc;
      if (!doc) return;

      if (doc.google_drive_id) {
        const { data: { session } } = await supabase.auth.getSession();
        console.log(doc.id);
        await fetch(
          `${supabase.supabaseUrl}/functions/v1/document-delete-google-drive`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session?.access_token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ document_id: doc.id })
          }
        );
      }

      if (doc.storage_path) {
        await supabase.storage
          .from('files')
          .remove([doc.storage_path]);
      }

      const { error } = await supabase
        .from('document')
        .delete()
        .eq('id', doc.id);

      if (error) throw error;

      if (onDocumentsChange) await onDocumentsChange();

      setDeleteDocDlg({ open: false, doc: null, deleting: false });

    } catch (err) {
      console.error('Error eliminando documento:', err);

      setDeleteDocDlg(p => ({ ...p, deleting: false }));
    }
  };

  // ── Document actions ───────────────────────────────────────────────────────

  const onDocAction = useCallback(async (action, doc) => {
    if (action === 'download') {
      setDownloading(p => ({ ...p, [doc.id]:true }));
      try {
        if (doc.google_drive_id) {
          const { data:{ session } } = await supabase.auth.getSession();
          const res = await fetch(
            `${supabase.supabaseUrl}/functions/v1/document-download-google-drive?document_id=${doc.id}`,
            { headers:{ Authorization:`Bearer ${session?.access_token}` } }
          );
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const url  = URL.createObjectURL(blob);
          const a    = Object.assign(document.createElement('a'), { href:url, download:doc.filename });
          document.body.appendChild(a); a.click();
          URL.revokeObjectURL(url); document.body.removeChild(a);
        } else if (doc.storage_path) {
          const { data, error } = await supabase.storage.from('files').createSignedUrl(doc.storage_path, 60);
          if (error) throw error;
          window.open(data.signedUrl, '_blank');
        }
      } catch (err) { alert(err.message); }
      finally { setDownloading(p => ({ ...p, [doc.id]:false })); }
    }
    if (action === 'archive')   setArchiveDlg({ open:true, doc, archiving:false });
    if (action === 'unarchive') {
      try {
        await supabase.from('document').update({ is_archived:false }).eq('id', doc.id);
        if (onDocumentsChange) await onDocumentsChange();
      } catch { alert('Error al desarchivar'); }
    }
    if (action === 'delete') {
      setDeleteDocDlg({ open: true, doc, deleting: false });
      return;
    }
    if (action === 'analyze') {
      setAnalyzingId(doc.id);
      setAnalysisDlg({ open: true, doc, text: '', loading: true, error: '' });
      try {
        const reply = await analyzeDocument(doc.id);
        setAnalysisDlg({ open: true, doc, text: reply, loading: false, error: '' });
      } catch (err) {
        setAnalysisDlg({ open: true, doc, text: '', loading: false, error: err.message || 'Error al analizar el documento' });
      } finally {
        setAnalyzingId(null);
      }
    }
  }, [onDocumentsChange]);

  const doArchive = async () => {
    setArchiveDlg(p => ({ ...p, archiving:true }));
    try {
      await supabase.from('document').update({ is_archived:true }).eq('id', archiveDlg.doc.id);
      if (onDocumentsChange) await onDocumentsChange();
      setArchiveDlg({ open:false, doc:null, archiving:false });
    } catch { alert('Error'); setArchiveDlg(p => ({ ...p, archiving:false })); }
  };

  const handleCreateGoogleDoc = async (docName, folderId) => {
    setCreatingGoogleDoc(true);
    setGoogleDocError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();

      const res = await fetch(
        `${supabase.supabaseUrl}/functions/v1/document-create-google-doc`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_id:    caseData.id,
            office_id:  office.id,
            name:       docName,
            created_by: currentUser?.id,
            folder_id:  folderId || null,
          }),
        }
      );

      const result = await res.json();

      if (!res.ok || !result.success) {
        throw new Error(result.error || 'Error al crear el documento en Google Drive');
      }

      const targetSpaceId = folderId ? (folderMap[folderId]?.space_id || defaultSpaceId) : defaultSpaceId;
      if (result.document?.id && targetSpaceId) {
        await supabase.from('document').update({ space_id: targetSpaceId }).eq('id', result.document.id);
      }

      // Refrescar lista de documentos
      if (onDocumentsChange) await onDocumentsChange();

      // Cerrar diálogo
      setOpenGoogleDocDlg(false);

      // Abrir el documento recién creado en Drive en nueva pestaña
      if (result.document?.drive_url || result.document?.google_drive_id) {
        const editUrl = result.document.drive_url?.replace('/view', '/edit')
          || `https://docs.google.com/document/d/${result.document.google_drive_id}/edit`;
        window.open(editUrl, '_blank', 'noopener');
      }

    } catch (err) {
      console.error('Error creando Google Doc:', err);
      setGoogleDocError(err.message);
      throw err; // Para que el diálogo muestre el error
    } finally {
      setCreatingGoogleDoc(false);
    }
  };

  // ── Upload individual ──────────────────────────────────────────────────────

  const handleUpload = async (file, _name, folderId) => {
    const { data:{ session } } = await supabase.auth.getSession();
    const targetSpaceId = uploadSpaceId || defaultSpaceId;
    let result;

    if (storageType !== 'own') {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('case_id', caseData.id);
      fd.append('office_id', office.id);
      fd.append('fileName', file.name);
      fd.append('created_by', currentUser?.id || '');
      if (folderId) fd.append('folder_id', folderId);
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/document-upload-google-drive`,
        { method:'POST', headers:{ Authorization:`Bearer ${session?.access_token}` }, body: fd });
      result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.error || 'Error al subir');
      if (result.document?.id && targetSpaceId) {
        await supabase.from('document').update({ space_id: targetSpaceId }).eq('id', result.document.id);
      }
    } else {
      // Supabase storage
      const ts   = Date.now();
      const ext  = file.name.split('.').pop();
      const base = file.name.replace(`.${ext}`,'');
      const path = `office/${office.id}/case/${caseData.id}/${base}_${ts}.${ext}`;
      const { error: upErr } = await supabase.storage.from('files').upload(path, file);
      if (upErr) throw upErr;
      const { data, error: dbErr } = await supabase.from('document').insert({
        case_id: caseData.id, name: file.name, filename:`${base}_${ts}.${ext}`,
        storage_path: path, extension: ext, mime_type: file.type,
        size_bytes: file.size, created_by: currentUser?.id, folder_id: folderId || null,
        space_id: targetSpaceId,
      }).select().single();
      if (dbErr) { await supabase.storage.from('files').remove([path]); throw dbErr; }
      result = { success:true, document: data };
    }

    if (onDocumentsChange) await onDocumentsChange();
    if (result.document?.id) {
      await promptForMetadata({
        id: result.document.id,
        name: result.document.name || result.document.filename || file.name,
      });
    }
    return result;
  };

  // ── Acciones de sección (árbol de documentos) ─────────────────────────────

  const doCreateSpace = async (name) => createSpace(name, currentUser?.id);
  const doRenameSpace = async (name) => renameSpace(renameSpaceDlg.space?.id, name);
  const doDeleteSpace = async () => {
    setDeleteSpaceDlg(p => ({ ...p, deleting: true }));
    try {
      await deleteSpace(deleteSpaceDlg.space?.id);
      setDeleteSpaceDlg({ open:false, space:null, deleting:false });
    } catch (e) {
      alert(e.message);
      setDeleteSpaceDlg(p => ({ ...p, deleting:false }));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const hasDrive     = display.some(d => d.google_drive_id);
  const totalActive  = activeDocs.length;
  const totalArch    = archDocs.length;
  const canCreateGoogleDoc = !isReadOnly() && caseRole !== 'client' && storageType !== 'own';

  return (
    <Paper sx={{ p: 3, mt: 3 }} onDragEnd={onDragEnd}>
      {/* Header */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 2, flexWrap:'wrap', gap: 1 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
          <Typography variant="h6">{t.caseDocuments || 'Documentos del Expediente'}</Typography>
          <Chip label={totalActive} size="small"
            sx={{ backgroundColor:`${officeColor}20`, color: officeColor, fontWeight:'bold' }}/>
          {totalArch > 0 && (
            <Chip label={`${totalArch} archivados`} size="small" variant="outlined"
              sx={{ fontSize:'0.7rem', color:'#ff9800', borderColor:'#ff9800' }}/>
          )}
        </Box>

        {!isReadOnly() && (
          <Box sx={{ display:'flex', gap: 1, flexWrap:'wrap', alignItems:'center' }}>
            <Button variant="outlined" size="small" startIcon={<ZipIcon/>}
              onClick={() => setOpenZip(true)}
              sx={{ borderColor: officeColor, color: officeColor, fontSize:'0.8rem' }}>
              Subir ZIP
            </Button>
             {canCreateGoogleDoc && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<GoogleDocIcon />}
                onClick={() => setOpenGoogleDocDlg(true)}
                sx={{
                  borderColor: '#4285f4',
                  color: '#4285f4',
                  fontSize: '0.8rem',
                  '&:hover': { backgroundColor: '#e8f0fe', borderColor: '#3367d6' },
                }}
              >
                Nuevo Google Doc
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Tabs */}
      {caseRole !== 'client' && totalArch > 0 && (
        <Box sx={{ display:'flex', gap: 1, mb: 2 }}>
          {[
            { label:`Activos (${totalActive})`,   val:false, color: officeColor },
            { label:`Archivados (${totalArch})`,  val:true,  color:'#ff9800'    },
          ].map(tab => (
            <Chip key={String(tab.val)} label={tab.label} size="small"
              onClick={() => setShowArchived(tab.val)}
              variant={showArchived === tab.val ? 'filled' : 'outlined'}
              sx={showArchived === tab.val
                ? { backgroundColor: tab.color, color:'white' }
                : { borderColor: tab.color, color: tab.color }}
            />
          ))}
        </Box>
      )}

      {/* ── Secciones (árboles de documentos) ── */}
      {spaceSections.map((section, idx) => (
        <Box key={section.space.id} sx={{ mb: idx < spaceSections.length - 1 ? 4 : 0 }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 1.25, flexWrap:'wrap', gap: 1 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>{section.space.name}</Typography>
              <Chip label={section.display.length} size="small"
                sx={{ backgroundColor:`${officeColor}20`, color: officeColor, fontWeight:'bold' }}/>
              {!isReadOnly() && (
                <Tooltip title="Renombrar sección">
                  <IconButton size="small"
                    onClick={() => setRenameSpaceDlg({ open:true, space: section.space })}
                    sx={{ color: 'text.secondary', p: 0.4 }}>
                    <RenameIcon sx={{ fontSize: 15 }}/>
                  </IconButton>
                </Tooltip>
              )}
              {!isReadOnly() && spaceSections.length > 1 && (
                <Tooltip title={section.isTrulyEmpty ? 'Eliminar sección' : 'Vacía la sección para poder eliminarla'}>
                  <span>
                    <IconButton size="small"
                      disabled={!section.isTrulyEmpty}
                      onClick={() => setDeleteSpaceDlg({ open:true, space: section.space, deleting:false })}
                      sx={{ color: section.isTrulyEmpty ? '#f44336' : 'text.disabled', p: 0.4 }}>
                      <DeleteIcon sx={{ fontSize: 15 }}/>
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
            {!isReadOnly() && (
              <Box sx={{ display:'flex', gap: 1 }}>
                <Tooltip title="Crear carpeta en raíz">
                  <IconButton size="small"
                    onClick={() => setCreateDlg({ open:true, parentId:null, title:`Nueva carpeta en raíz (${section.space.name})`, spaceId: section.space.id })}
                    sx={{ border:`1px solid ${officeColor}50`, color: officeColor, borderRadius: 1.5, p: 0.6 }}>
                    <CreateNewFolderIcon sx={{ fontSize: 18 }}/>
                  </IconButton>
                </Tooltip>
                <Button variant="contained" size="small" startIcon={<AddIcon/>}
                  onClick={() => { setUploadSpaceId(section.space.id); setOpenUpload(true); }}
                  sx={{ backgroundColor: officeColor, '&:hover':{ backgroundColor: officeColor, opacity:0.9 } }}>
                  {t.addDocument || 'Agregar documento'}
                </Button>
              </Box>
            )}
          </Box>

          {!isReadOnly() && caseRole !== 'client' && !section.isEmpty && (
            <Box sx={{ display:'flex', alignItems:'center', gap: 0.75, mb: 1.5, opacity: 0.55 }}>
              <DragIcon sx={{ fontSize:13, color:'text.secondary' }}/>
              <Typography variant="caption" color="text.secondary">
                Arrastra archivos o carpetas para reorganizar
              </Typography>
            </Box>
          )}

          {section.isEmpty ? (
            <Box
              onDragEnter={e => { e.preventDefault(); onDragEnter(section.rootId); }}
              onDragOver={e  => { e.preventDefault(); onDragEnter(section.rootId); }}
              onDragLeave={() => onDragLeave(section.rootId)}
              onDrop={e => { e.preventDefault(); onDrop(section.rootId); }}
              sx={{
                textAlign:'center', py: 5, borderRadius: 2,
                border: drag.overId === section.rootId ? `2px dashed ${officeColor}` : '1px dashed #e0e0e0',
                backgroundColor: drag.overId === section.rootId ? `${officeColor}0e` : 'transparent',
                transition: 'all 0.12s',
              }}
            >
              <UploadFileIcon sx={{ fontSize:36, opacity:0.2, mb:1, display:'block', mx:'auto' }}/>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {drag.overId === section.rootId
                  ? 'Soltar aquí'
                  : showArchived ? 'No hay documentos archivados' : `Aún no hay documentos en "${section.space.name}"`}
              </Typography>
              {!isReadOnly() && !showArchived && drag.overId !== section.rootId && (
                <Typography variant="caption" color="text.secondary">
                  Usa los botones de arriba para agregar archivos o crear carpetas
                  {spaceSections.length > 1 ? ', o arrastra documentos desde otra sección.' : '.'}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ border:'1px solid #e8ecf0', borderRadius: 2, p: 1, backgroundColor:'#fafbfc' }}>
              <FolderNode node={section.vRoot} depth={0}
                officeColor={officeColor} onDocAction={onDocAction} onFolderAction={onFolderAction}
                downloading={downloading} caseRole={caseRole} isReadOnly={isReadOnly()}
                caseData={caseData} t={t} dragState={drag}
                onDragStart={onDragStart} onDragEnter={onDragEnter}
                onDragLeave={onDragLeave} onDrop={onDrop}
                analyzingId={analyzingId}
              />
            </Box>
          )}

          {idx < spaceSections.length - 1 && <Divider sx={{ mt: 4 }} />}
        </Box>
      ))}

      {!isReadOnly() && (
        <Box sx={{ mt: spaceSections.length > 0 ? 3 : 0 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<AddSectionIcon sx={{ fontSize: 18 }} />}
            onClick={() => setCreateSpaceDlg(true)}
            sx={{ color: officeColor, fontWeight: 600, fontSize: '0.8rem', textTransform: 'none' }}
          >
            Agregar sección de documentos
          </Button>
        </Box>
      )}

      {hasDrive && (
        <Alert severity="info" sx={{ mt: 2, fontSize:'0.78rem', py: 0.5 }}>
          <strong>Google Drive:</strong> Los documentos abren en nueva pestaña.
          Archivos Word se pueden editar en Google Docs. Necesitas la cuenta de Google con permisos en este expediente.
        </Alert>
      )}

      {/* Modales */}
      <DocumentUploadModal
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        onUpload={handleUpload}
        officeColor={officeColor}
        language={caseData?.language}
        readOnly={isReadOnly()}
        flatFolders={flatFolders}
      />
      <ZipUploadModal
        open={openZip}
        onClose={() => setOpenZip(false)}
        caseData={caseData} office={office} currentUser={currentUser}
        flatFolders={flatFolders}
        onComplete={async () => { if (onDocumentsChange) await onDocumentsChange(); await loadFolders(); }}
      />
      <DocumentMetadataModal
        open={!!metadataDoc}
        documentName={metadataDoc?.name}
        saving={savingMetadata}
        onSave={handleMetadataSave}
        onSkip={handleMetadataSkip}
      />

      <CreateGoogleDocDialog
        open={openGoogleDocDlg}
        onClose={() => { setOpenGoogleDocDlg(false); setGoogleDocError(''); }}
        onConfirm={handleCreateGoogleDoc}
        officeColor={officeColor}
        flatFolders={flatFolders}
        spaces={spaces}
        creating={creatingGoogleDoc}
      />

      {/* Diálogos carpetas */}
      <FolderNameDialog open={createDlg.open} onClose={() => setCreateDlg({ open:false, parentId:null, title:'', spaceId:null })}
        onConfirm={doCreate} title={createDlg.title} officeColor={officeColor}/>
      <FolderNameDialog open={renameDlg.open} onClose={() => setRenameDlg({ open:false, folder:null })}
        onConfirm={doRename} title={`Renombrar "${renameDlg.folder?.name}"`}
        initialValue={renameDlg.folder?.name || ''} officeColor={officeColor}/>

      {/* Diálogos de sección */}
      <FolderNameDialog open={createSpaceDlg} onClose={() => setCreateSpaceDlg(false)}
        onConfirm={doCreateSpace} title="Nueva sección de documentos" label="Nombre de la sección" officeColor={officeColor}/>
      <FolderNameDialog open={renameSpaceDlg.open} onClose={() => setRenameSpaceDlg({ open:false, space:null })}
        onConfirm={doRenameSpace} title={`Renombrar "${renameSpaceDlg.space?.name}"`} label="Nombre de la sección"
        initialValue={renameSpaceDlg.space?.name || ''} officeColor={officeColor}/>

      <Dialog open={deleteSpaceDlg.open} onClose={() => !deleteSpaceDlg.deleting && setDeleteSpaceDlg({ open:false, space:null, deleting:false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <WarningIcon sx={{ color:'#ff9800' }}/>Eliminar sección
        </DialogTitle>
        <DialogContent>
          <DialogContentText>¿Eliminar la sección <strong>"{deleteSpaceDlg.space?.name}"</strong>?</DialogContentText>
          <Alert severity="info" sx={{ mt:2, fontSize:'0.8rem' }}>
            Está vacía, así que se puede eliminar sin perder ningún documento.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}>
          <Button onClick={() => setDeleteSpaceDlg({ open:false, space:null, deleting:false })} variant="outlined" size="small" disabled={deleteSpaceDlg.deleting}>Cancelar</Button>
          <Button onClick={doDeleteSpace} variant="contained" size="small" disabled={deleteSpaceDlg.deleting}
            startIcon={deleteSpaceDlg.deleting ? <CircularProgress size={14}/> : <DeleteIcon/>}
            sx={{ backgroundColor:'#f44336', '&:hover':{ backgroundColor:'#d32f2f' } }}>
            {deleteSpaceDlg.deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDlg.open} onClose={() => !deleteDlg.deleting && setDeleteDlg({ open:false, folder:null, deleting:false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <WarningIcon sx={{ color:'#ff9800' }}/>Eliminar carpeta
        </DialogTitle>
        <DialogContent>
          <DialogContentText>¿Eliminar <strong>"{deleteDlg.folder?.name}"</strong>?</DialogContentText>
          <Alert severity="warning" sx={{ mt:2, fontSize:'0.8rem' }}>
            Los archivos dentro quedarán en la raíz. Las subcarpetas también se eliminarán.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}>
          <Button onClick={() => setDeleteDlg({ open:false, folder:null, deleting:false })} variant="outlined" size="small" disabled={deleteDlg.deleting}>Cancelar</Button>
          <Button onClick={doDelete} variant="contained" size="small" disabled={deleteDlg.deleting}
            startIcon={deleteDlg.deleting ? <CircularProgress size={14}/> : <DeleteIcon/>}
            sx={{ backgroundColor:'#f44336', '&:hover':{ backgroundColor:'#d32f2f' } }}>
            {deleteDlg.deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={archiveDlg.open} onClose={() => !archiveDlg.archiving && setArchiveDlg({ open:false, doc:null, archiving:false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <WarningIcon sx={{ color: officeColor }}/>{t.confirmArchive || 'Confirmar archivo'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t.archiveDocumentConfirmation || '¿Deseas archivar este documento?'}</DialogContentText>
          {archiveDlg.doc && (
            <Box sx={{ mt:2, p:1.5, backgroundColor:'#f5f5f5', borderRadius:1 }}>
              <Typography variant="body2" fontWeight="bold">{archiveDlg.doc.name || archiveDlg.doc.filename}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}>
          <Button onClick={() => setArchiveDlg({ open:false, doc:null, archiving:false })} variant="outlined" size="small" disabled={archiveDlg.archiving}>Cancelar</Button>
          <Button onClick={doArchive} variant="contained" size="small" disabled={archiveDlg.archiving}
            startIcon={archiveDlg.archiving ? <CircularProgress size={14}/> : <ArchiveIcon/>}
            sx={{ backgroundColor: officeColor }}>
            {archiveDlg.archiving ? 'Archivando...' : 'Archivar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDocDlg.open}
        onClose={() => !deleteDocDlg.deleting && setDeleteDocDlg({ open:false, doc:null, deleting:false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <WarningIcon sx={{ color:'#f44336' }}/>
          Eliminar documento
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar este documento?
          </DialogContentText>

          {deleteDocDlg.doc && (
            <Box sx={{ mt:2, p:1.5, backgroundColor:'#f5f5f5', borderRadius:1 }}>
              <Typography variant="body2" fontWeight="bold">
                {deleteDocDlg.doc.name || deleteDocDlg.doc.filename}
              </Typography>
            </Box>
          )}

          <Alert severity="warning" sx={{ mt:2, fontSize:'0.8rem' }}>
            Esta acción no se puede deshacer.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px:3, pb:2 }}>
          <Button
            onClick={() => setDeleteDocDlg({ open:false, doc:null, deleting:false })}
            variant="outlined"
            size="small"
            disabled={deleteDocDlg.deleting}
          >
            Cancelar
          </Button>

          <Button
            onClick={doDeleteDocument}
            variant="contained"
            size="small"
            disabled={deleteDocDlg.deleting}
            startIcon={deleteDocDlg.deleting ? <CircularProgress size={14}/> : <DeleteIcon/>}
            sx={{ backgroundColor:'#f44336', '&:hover':{ backgroundColor:'#d32f2f' } }}
          >
            {deleteDocDlg.deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={analysisDlg.open}
        onClose={() => setAnalysisDlg({ open: false, doc: null, text: '', loading: false, error: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: '#6c72ff' }} />
          Análisis IA — {analysisDlg.doc?.name || analysisDlg.doc?.filename}
        </DialogTitle>
        <DialogContent>
          {analysisDlg.loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Analizando documento…</Typography>
            </Box>
          ) : analysisDlg.error ? (
            <Alert severity="error">{analysisDlg.error}</Alert>
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {analysisDlg.text}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAnalysisDlg({ open: false, doc: null, text: '', loading: false, error: '' })}
            variant="outlined"
            size="small"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
